/**
 * Product evolution tab — track sales of one or several products over time.
 *
 * UX choices (validated with the user):
 *   1c — Top N by metric, with manual add/remove
 *   2c — Toggle Qté / CA
 *   3a — Toggle Jour / Semaine / Mois
 *   4c — Toggle Agrégé / Par site
 *   5b — Chart + recap table with Δ% vs previous period
 *
 * Backend: GET /api/sales/product-evolution
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, TrendingUp, TrendingDown, X, Plus, Search, Sigma, LineChart as LineChartIcon } from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { salesAPI, sitesAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const FE_NAMES = ['FE Génipa', 'FE Cluny', 'FE Dillon', 'FE Océanis', 'FE Vauclin'];
// 10 distinct colors for product series — friendly with both light/dark series labels
const SERIES_COLORS = [
    '#0ea5e9', // sky
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#14b8a6', // teal
    '#f97316', // orange
    '#6366f1', // indigo
    '#84cc16', // lime
];

export default function ProductEvolutionTab() {
    const [sites, setSites] = useState([]);
    const [siteIds, setSiteIds] = useState(new Set()); // empty = all FE
    const [granularity, setGranularity] = useState('day');     // day | week | month
    const [metric, setMetric] = useState('ca');                // ca | qty
    const [groupBySite, setGroupBySite] = useState(false);
    const [topN, setTopN] = useState(5);
    const [pickedCodes, setPickedCodes] = useState([]);        // [] => use top N
    const [search, setSearch] = useState('');
    const today = new Date().toISOString().slice(0, 10);
    const startDefault = (() => {
        const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10);
    })();
    const [startDate, setStartDate] = useState(startDefault);
    const [endDate, setEndDate] = useState(today);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await sitesAPI.getAll();
                const fe = (r.data || []).filter(s => FE_NAMES.includes(s.name))
                    .sort((a, b) => FE_NAMES.indexOf(a.name) - FE_NAMES.indexOf(b.name));
                setSites(fe);
            } catch (e) { /* silent */ }
        })();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                start_date: startDate,
                end_date: endDate,
                granularity,
                top_n: topN,
                top_metric: metric,
                group_by_site: groupBySite,
                include_previous: true,
            };
            if (siteIds.size > 0) params.site_ids = [...siteIds].join(',');
            if (pickedCodes.length > 0) params.code_metis = pickedCodes.join(',');
            const r = await salesAPI.productEvolution(params);
            setData(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || 'Erreur lors du chargement');
        } finally { setLoading(false); }
    };

    // Refetch on any config change (debounced)
    useEffect(() => {
        const id = setTimeout(fetchData, 250);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, granularity, metric, groupBySite, topN, siteIds, pickedCodes]);

    // ----- Build Recharts data: one row per bucket, one field per series -----
    const chartData = useMemo(() => {
        if (!data) return [];
        return data.buckets.map(b => {
            const row = { bucket: b };
            for (const s of data.series) {
                const v = s.data?.[b];
                row[s.key] = v ? (metric === 'ca' ? v.ca : v.qty) : null;
            }
            return row;
        });
    }, [data, metric]);

    const seriesLabel = (s) => {
        if (groupBySite && s.site_name) {
            const short = s.site_name.replace('FE ', '');
            return `${s.product_name} · ${short}`;
        }
        return s.product_name;
    };

    const toggleSite = (id) => {
        setSiteIds(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const removePickedCode = (code) => {
        setPickedCodes(prev => prev.filter(c => c !== code));
    };
    const addPickedCode = (code) => {
        setPickedCodes(prev => prev.includes(code) ? prev : [...prev, code]);
        setSearch('');
    };
    const useTopN = () => setPickedCodes([]);

    // Unique code_metis currently plotted (one chip per product, even in by-site mode).
    const displayedCodes = useMemo(() => {
        if (!data?.series) return [];
        const seen = [];
        const set = new Set();
        for (const s of data.series) {
            if (!set.has(s.code_meti)) { set.add(s.code_meti); seen.push(s.code_meti); }
        }
        return seen;
    }, [data]);

    // Remove a chip — whether it came from Top N or from explicit picks:
    //   - In Top N mode, freeze the current top into pickedCodes minus the removed one (switches to explicit mode).
    //   - In explicit mode, just drop the code.
    const removeDisplayedCode = (code) => {
        if (pickedCodes.length === 0) {
            setPickedCodes(displayedCodes.filter(c => c !== code));
        } else {
            removePickedCode(code);
        }
    };

    // Search results for the picker (excludes already-displayed)
    const availableForSearch = useMemo(() => {
        if (!data?.available_products) return [];
        const q = search.trim().toLowerCase();
        const exclude = new Set(displayedCodes);
        return data.available_products
            .filter(p => !exclude.has(p.code_meti))
            .filter(p => !q || p.product_name.toLowerCase().includes(q) || p.code_meti.includes(q))
            .slice(0, 20);
    }, [data, search, displayedCodes]);

    // ----- Quick-range presets aligned with the rest of the Sales pages -----
    const applyRange = (rangeKey) => {
        const today = new Date();
        const d2 = today.toISOString().slice(0, 10);
        const start = new Date(today);
        if (rangeKey === '7d') start.setDate(start.getDate() - 6);
        else if (rangeKey === '30d') start.setDate(start.getDate() - 29);
        else if (rangeKey === '90d') start.setDate(start.getDate() - 89);
        else if (rangeKey === 'ytd') { start.setMonth(0); start.setDate(1); }
        else if (rangeKey === '12m') start.setMonth(start.getMonth() - 12);
        else if (rangeKey === '24m') start.setMonth(start.getMonth() - 24);
        else if (rangeKey === '36m') start.setMonth(start.getMonth() - 36);
        const d1 = start.toISOString().slice(0, 10);
        setStartDate(d1); setEndDate(d2);
    };

    return (
        <div className="space-y-3" data-testid="product-evolution-tab">
            {/* ─── Controls bar ─── */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <LineChartIcon className="w-4 h-4 text-purple-500" />
                        Évolution des produits
                        <span className="text-xs text-slate-400 font-normal ml-2">
                            {pickedCodes.length > 0
                                ? `${pickedCodes.length} produit(s) sélectionné(s)`
                                : `Top ${topN} par ${metric === 'ca' ? 'CA' : 'Quantité'}`}
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    {/* Row 1: site shortcuts */}
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] font-semibold text-slate-500 mr-1">Sites :</span>
                        <button
                            type="button"
                            onClick={() => setSiteIds(new Set())}
                            className={`px-2.5 h-6 rounded-full text-[11px] font-semibold border ${siteIds.size === 0 ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                            data-testid="pe-site-all"
                        >Tous FE</button>
                        {sites.map(s => {
                            const short = s.name.replace('FE ', '');
                            const active = siteIds.has(s.id);
                            return (
                                <button key={s.id} type="button" onClick={() => toggleSite(s.id)}
                                    className={`px-2.5 h-6 rounded-full text-[11px] font-semibold border ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                                    data-testid={`pe-site-${short.toLowerCase()}`}
                                >{short}</button>
                            );
                        })}
                    </div>

                    {/* Row 2: granularity / metric / group / top N / quick ranges */}
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        <SegmentedToggle
                            label="Période"
                            options={[
                                { v: 'day', l: 'Jour' },
                                { v: 'week', l: 'Sem.' },
                                { v: 'month', l: 'Mois' },
                            ]}
                            value={granularity}
                            onChange={setGranularity}
                            testid="pe-granularity"
                        />
                        <SegmentedToggle
                            label="Métrique"
                            options={[
                                { v: 'ca', l: 'CA' },
                                { v: 'qty', l: 'Qté' },
                            ]}
                            value={metric}
                            onChange={setMetric}
                            testid="pe-metric"
                        />
                        <SegmentedToggle
                            label="Regroupement"
                            options={[
                                { v: 'agg', l: 'Agrégé' },
                                { v: 'site', l: 'Par site' },
                            ]}
                            value={groupBySite ? 'site' : 'agg'}
                            onChange={(v) => setGroupBySite(v === 'site')}
                            testid="pe-group"
                        />
                        {pickedCodes.length === 0 && (
                            <div className="inline-flex items-center gap-1">
                                <span className="text-[11px] font-semibold text-slate-500">Top :</span>
                                <select value={topN} onChange={e => setTopN(parseInt(e.target.value))}
                                    className="h-6 border border-slate-200 rounded text-[11px] px-1"
                                    data-testid="pe-top-n"
                                >
                                    {[3, 5, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="inline-flex items-center gap-1 ml-auto">
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                className="h-7 border border-slate-200 rounded text-[11px] px-1" data-testid="pe-start-date" />
                            <span className="text-slate-400">→</span>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                                className="h-7 border border-slate-200 rounded text-[11px] px-1" data-testid="pe-end-date" />
                            {['7d', '30d', '90d', 'ytd', '12m', '24m', '36m'].map(k => (
                                <button key={k} type="button" onClick={() => applyRange(k)}
                                    className="px-2 h-6 rounded text-[10px] font-semibold border border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                                    title={k === 'ytd' ? 'Depuis le 1er janvier' : k.endsWith('m') ? `${k.replace('m', '')} derniers mois` : `${k.replace('d', '')} derniers jours`}
                                    data-testid={`pe-range-${k}`}
                                >{k.toUpperCase()}</button>
                            ))}
                        </div>
                    </div>

                    {/* Row 3: product picker (chips + search) */}
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] font-semibold text-slate-500 mr-1">Produits :</span>
                        {pickedCodes.length === 0 && (
                            <Badge variant="outline" className="text-[10px] bg-slate-50">Top {topN} ({metric === 'ca' ? 'CA' : 'Quantité'})</Badge>
                        )}
                        {/* Chips for currently displayed products (from Top N or explicit picks).
                            Clicking X on a Top-N item switches into explicit mode minus that code. */}
                        {displayedCodes.map((code) => {
                            const s = data?.series.find(x => x.code_meti === code);
                            const color = s ? SERIES_COLORS[s.color_idx % SERIES_COLORS.length] : '#94a3b8';
                            const label = s?.product_name || code;
                            return (
                                <span key={code}
                                    className="inline-flex items-center gap-1 pl-1.5 pr-1 h-6 rounded-full bg-white border text-[11px]"
                                    style={{ borderColor: color, color: '#334155' }}
                                >
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                                    {label?.slice(0, 28) || code}
                                    <button type="button" onClick={() => removeDisplayedCode(code)}
                                        className="hover:bg-slate-100 rounded-full p-0.5"
                                        data-testid={`pe-remove-${code}`}
                                        title="Retirer cette courbe"
                                    ><X className="w-3 h-3" /></button>
                                </span>
                            );
                        })}
                        {pickedCodes.length > 0 && (
                            <button type="button" onClick={useTopN}
                                className="px-2 h-6 rounded-full text-[10px] font-semibold border border-dashed border-slate-300 text-slate-500 hover:border-slate-400"
                                data-testid="pe-reset-to-top"
                            >Revenir au Top {topN}</button>
                        )}
                        <div className="relative ml-1 flex-1 min-w-[180px] max-w-[320px]">
                            <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                            <Input placeholder="Ajouter un produit…" value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="h-7 pl-6 text-[11px]"
                                data-testid="pe-search-input"
                            />
                            {search && availableForSearch.length > 0 && (
                                <div className="absolute top-8 left-0 right-0 z-30 bg-white border border-slate-200 rounded shadow-lg max-h-60 overflow-auto">
                                    {availableForSearch.map(p => (
                                        <button key={p.code_meti} type="button"
                                            onClick={() => addPickedCode(p.code_meti)}
                                            className="w-full px-2 py-1 text-left text-[11px] hover:bg-purple-50 flex items-center gap-1.5 border-b border-slate-100 last:border-b-0"
                                            data-testid={`pe-pick-${p.code_meti}`}
                                        >
                                            <Plus className="w-3 h-3 text-purple-500 flex-shrink-0" />
                                            <span className="font-mono text-slate-400">{p.code_meti}</span>
                                            <span className="flex-1 truncate">{p.product_name}</span>
                                            <span className="text-slate-400 tabular-nums">{formatCurrency(p.ca)}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ─── Chart ─── */}
            <Card>
                <CardContent className="p-2">
                    {loading ? (
                        <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
                    ) : error ? (
                        <div className="py-8 text-center text-red-500 text-sm">{error}</div>
                    ) : !data || data.series.length === 0 ? (
                        <div className="py-16 text-center text-slate-400 text-sm">
                            <Sigma className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                            Aucune donnée sur la période sélectionnée
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={360} data-testid="pe-chart">
                            <LineChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                                <YAxis
                                    tickFormatter={(v) => {
                                        if (v == null) return '';
                                        if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k`;
                                        return v.toFixed(0);
                                    }}
                                    tick={{ fontSize: 10, fill: '#64748b' }}
                                    width={48}
                                />
                                <Tooltip
                                    contentStyle={{ fontSize: 11, padding: '6px 8px' }}
                                    labelStyle={{ fontSize: 11, color: '#1e293b', fontWeight: 600 }}
                                    formatter={(value, name) => {
                                        if (value == null) return ['—', name];
                                        return [metric === 'ca' ? formatCurrency(value) : value.toFixed(0), name];
                                    }}
                                />
                                <Legend
                                    wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                                    formatter={(_value, entry) => {
                                        const s = data.series.find(x => x.key === entry.dataKey);
                                        return s ? seriesLabel(s) : entry.dataKey;
                                    }}
                                />
                                {data.series.map((s) => (
                                    <Line
                                        key={s.key}
                                        type="monotone"
                                        dataKey={s.key}
                                        name={seriesLabel(s)}
                                        stroke={SERIES_COLORS[s.color_idx % SERIES_COLORS.length]}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                        connectNulls={false}
                                        isAnimationActive={false}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {/* ─── Recap table ─── */}
            {data && data.series.length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Sigma className="w-3.5 h-3.5 text-slate-400" />
                            Récapitulatif sur la période
                            <span className="text-[10px] font-normal text-slate-400">
                                vs période précédente de même durée
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-1.5 w-6">#</th>
                                    <th className="text-left px-3 py-1.5">Produit</th>
                                    {groupBySite && <th className="text-left px-3 py-1.5 w-20">Site</th>}
                                    <th className="text-right px-3 py-1.5 w-20">Qté</th>
                                    <th className="text-right px-3 py-1.5 w-16">Δ%</th>
                                    <th className="text-right px-3 py-1.5 w-24">CA</th>
                                    <th className="text-right px-3 py-1.5 w-16">Δ%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.series.map((s, idx) => {
                                    const bg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-100';
                                    const color = SERIES_COLORS[s.color_idx % SERIES_COLORS.length];
                                    return (
                                        <tr key={s.key} className={`${bg} hover:bg-blue-50 border-b border-slate-100`}>
                                            <td className="px-3 py-1">
                                                <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                                            </td>
                                            <td className="px-3 py-1 truncate max-w-[420px]" title={s.product_name}>
                                                <span className="font-mono text-[10px] text-slate-400 mr-1.5">{s.code_meti}</span>
                                                {s.product_name}
                                            </td>
                                            {groupBySite && (
                                                <td className="px-3 py-1 text-slate-500">{s.site_name?.replace('FE ', '') || '—'}</td>
                                            )}
                                            <td className="px-3 py-1 text-right tabular-nums">{(s.total?.qty || 0).toFixed(0)}</td>
                                            <td className="px-3 py-1 text-right tabular-nums"><Delta value={s.delta_qty_pct} /></td>
                                            <td className="px-3 py-1 text-right tabular-nums font-semibold">{formatCurrency(s.total?.ca || 0)}</td>
                                            <td className="px-3 py-1 text-right tabular-nums"><Delta value={s.delta_ca_pct} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// ─── small atoms ───

function SegmentedToggle({ label, options, value, onChange, testid }) {
    return (
        <div className="inline-flex items-center gap-1">
            <span className="text-[11px] font-semibold text-slate-500">{label} :</span>
            <div className="inline-flex bg-slate-200 rounded p-0.5 gap-0.5">
                {options.map(opt => (
                    <button key={opt.v} type="button" onClick={() => onChange(opt.v)}
                        className={`px-2 h-5 rounded text-[10px] font-semibold transition ${value === opt.v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                        data-testid={`${testid}-${opt.v}`}
                    >{opt.l}</button>
                ))}
            </div>
        </div>
    );
}

function Delta({ value }) {
    if (value == null) return <span className="text-slate-300">—</span>;
    const positive = value >= 0;
    const Icon = positive ? TrendingUp : TrendingDown;
    const cls = positive ? 'text-emerald-600' : 'text-red-500';
    return (
        <span className={`inline-flex items-center gap-0.5 justify-end ${cls}`}>
            <Icon className="w-3 h-3" />
            {value > 0 ? '+' : ''}{value.toFixed(1)}%
        </span>
    );
}
